import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc,
  query,
  orderBy,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { Line } from 'react-chartjs-2';

const MembershipManagement = ({ user, setCurrentPage }) => {
  // State variables
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [errorMessage, setErrorMessage] = useState('');
  const [stats, setStats] = useState({
    totalActive: 0,
    totalInactive: 0,
    monthly: 0,
    sixMonth: 0,
    yearly: 0,
    revenue: 0
  });
  const [revenueData, setRevenueData] = useState({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [{
      label: 'Monthly Revenue',
      data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      fill: false,
      backgroundColor: 'rgba(75, 192, 192, 0.2)',
      borderColor: 'rgba(75, 192, 192, 1)',
      tension: 0.4
    }]
  });

  // Subscription plans
  const subscriptionPlans = [
    { id: 'monthly', name: 'Monthly', price: 99 },
    { id: 'sixMonth', name: 'Six Month', price: 499 },
    { id: 'yearly', name: 'Yearly', price: 899 }
  ];

  // Subscription status options
  const subscriptionStatuses = [
    { id: 'active', name: 'Active' },
    { id: 'inactive', name: 'Inactive' }
  ];

  // Fetch memberships on component mount
  useEffect(() => {
    fetchMemberships();
  }, []);

  // Fetch memberships from Firestore
  const fetchMemberships = async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      let membershipsList = [];
      let activeCount = 0;
      let inactiveCount = 0;
      let monthlyCount = 0;
      let sixMonthCount = 0;
      let yearlyCount = 0;
      let totalRevenue = 0;
      
      // Revenue by month initialization
      const currentYear = new Date().getFullYear();
      const revenueByMonth = Array(12).fill(0);
      
      snapshot.docs.forEach(doc => {
        const userData = doc.data();
        
        // Only process users with subscription data
        if (userData.subscription) {
          const subscription = userData.subscription;
          
          // Format dates for display
          let startDate = subscription.startDate ? new Date(subscription.startDate) : null;
          let endDate = subscription.endDate ? new Date(subscription.endDate) : null;
          
          // Build membership object
          const membership = {
            id: doc.id,
            userId: userData.uid,
            displayName: userData.displayName,
            email: userData.email,
            instituteName: userData.instituteName,
            phone: userData.phone,
            planType: subscription.plan || 'monthly',
            status: subscription.status || 'inactive',
            startDate: startDate,
            endDate: endDate,
            amount: subscription.amount,
            orderId: subscription.orderId
          };
          
          // Count by status and plan type
          if (subscription.status === 'active') {
            activeCount++;
            // Add to revenue if active
            totalRevenue += subscription.amount || 0;
            
            // Add to monthly chart data if start date exists
            if (startDate && startDate.getFullYear() === currentYear) {
              const month = startDate.getMonth();
              revenueByMonth[month] += subscription.amount || 0;
            }
          } else {
            inactiveCount++;
          }
          
          // Count by plan type
          switch(subscription.plan) {
            case 'monthly':
              monthlyCount++;
              break;
            case 'sixMonth':
              sixMonthCount++;
              break;
            case 'yearly':
              yearlyCount++;
              break;
          }
          
          membershipsList.push(membership);
        }
      });
      
      // Update memberships list
      setMemberships(membershipsList);
      
      // Update statistics
      setStats({
        totalActive: activeCount,
        totalInactive: inactiveCount,
        monthly: monthlyCount,
        sixMonth: sixMonthCount,
        yearly: yearlyCount,
        revenue: totalRevenue
      });
      
      // Update revenue chart data
      setRevenueData(prevData => ({
        ...prevData,
        datasets: [{
          ...prevData.datasets[0],
          data: revenueByMonth
        }]
      }));
      
    } catch (error) {
      console.error('Error fetching memberships:', error);
      setErrorMessage('Failed to load memberships. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate subscription end date based on plan
  const calculateEndDate = (startDate, plan) => {
    const start = new Date(startDate);
    
    switch(plan) {
      case 'monthly':
        start.setMonth(start.getMonth() + 1);
        break;
      case 'sixMonth':
        start.setMonth(start.getMonth() + 6);
        break;
      case 'yearly':
        start.setFullYear(start.getFullYear() + 1);
        break;
      default:
        start.setMonth(start.getMonth() + 1);
    }
    
    // Format the date in the format "March 25, 2025 at 12:05:03 AM UTC-7"
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    };
    
    return start.toLocaleString('en-US', options);
  };

  // Handle updating membership
  const handleUpdateMembership = async (e) => {
    e.preventDefault();
    if (!selectedMembership) return;
    
    try {
      setLoading(true);
      
      // Calculate new end date if plan or start date changed
      let newEndDate = selectedMembership.endDate;
      if (selectedMembership.planType !== selectedMembership.originalPlanType ||
          selectedMembership.startDate !== selectedMembership.originalStartDate) {
        newEndDate = calculateEndDate(
          selectedMembership.startDate,
          selectedMembership.planType
        );
      }
      
      // Look up price for selected plan
      const planInfo = subscriptionPlans.find(p => p.id === selectedMembership.planType);
      const amount = planInfo ? planInfo.price : 99;
      
      // Update user in Firestore with updated subscription object
      const userRef = doc(db, 'users', selectedMembership.id);
      
      // Format the startDate as a string in the desired format
      const startDateFormatted = selectedMembership.startDate.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });
      
      await updateDoc(userRef, {
        "subscription.plan": selectedMembership.planType,
        "subscription.status": selectedMembership.status,
        "subscription.startDate": startDateFormatted,
        "subscription.endDate": newEndDate,
        "subscription.amount": amount,
        // Only generate a new orderId if plan type changed
        ...(selectedMembership.planType !== selectedMembership.originalPlanType ? {
          "subscription.orderId": `EC${Math.floor(Math.random() * 1000000000000000000)}`
        } : {})
      });
      
      // Close modal and refresh memberships
      setShowEditModal(false);
      fetchMemberships();
      
    } catch (error) {
      console.error('Error updating membership:', error);
      setErrorMessage('Failed to update membership: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Open edit modal for a membership
  const openEditModal = (membership) => {
    setSelectedMembership({
      ...membership,
      // Store original values for comparison
      originalPlanType: membership.planType,
      originalStatus: membership.status,
      originalStartDate: membership.startDate
    });
    setShowEditModal(true);
  };

  // Filter memberships based on search, plan type, and status
  const filteredMemberships = memberships.filter(membership => {
    const matchesSearch = 
      membership.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      membership.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      membership.instituteName?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesPlan = filterPlan === 'all' || membership.planType === filterPlan;
    const matchesStatus = filterStatus === 'all' || membership.status === filterStatus;
    
    return matchesSearch && matchesPlan && matchesStatus;
  });

  // Format date for display in the format "March 25, 2025 at 12:32:03 AM UTC-7"
  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    try {
      // Check if date is valid Date object
      if (date instanceof Date && !isNaN(date.getTime())) {
        // Format options for date with timezone
        const options = {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          timeZoneName: 'short'
        };
        
        return date.toLocaleString('en-US', options);
      }
      
      // Check if date is string in your format
      if (typeof date === 'string') {
        // Just return the string as is if it's already in the right format
        if (date.match(/^\w+ \d+, \d+ at \d+:\d+:\d+ \w+ UTC-\d+$/)) {
          return date;
        }
        
        // Try to create a date from the string
        const parsedDate = parseCustomDateFormat(date);
        if (parsedDate && !isNaN(parsedDate.getTime())) {
          const options = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'short'
          };
          
          return parsedDate.toLocaleString('en-US', options);
        }
      }
      
      return String(date);
    } catch (e) {
      console.error("Date formatting error:", e);
      return String(date) || 'N/A';
    }
  };
  
  // Custom parser for the specific date format "March 25, 2025 at 12:05:03 AM UTC-7"
  const parseCustomDateFormat = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return null;
    
    try {
      // Replace "at" with space and temporarily remove timezone for more reliable parsing
      const simplifiedDate = dateString.replace(" at ", " ").replace(/UTC-\d+/, "");
      const date = new Date(simplifiedDate);
      return date;
    } catch (e) {
      console.error("Error parsing custom date format:", e);
      return null;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return `$${amount.toFixed(2)}`;
  };

  // Get subscription badge color class
  const getSubscriptionBadgeClass = (plan, status) => {
    if (status !== 'active') {
      return 'bg-gray-100 text-gray-800';
    }
    
    switch (plan) {
      case 'yearly':
        return 'bg-green-100 text-green-800';
      case 'sixMonth':
        return 'bg-purple-100 text-purple-800';
      case 'monthly':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format plan name for display
  const formatPlanName = (plan) => {
    switch (plan) {
      case 'sixMonth':
        return 'Six Month';
      case 'monthly':
        return 'Monthly';
      case 'yearly':
        return 'Yearly';
      default:
        return plan;
    }
  };

  // Calculate subscription status
  const calculateStatus = (endDate) => {
    if (!endDate) return { text: 'Unknown', className: 'bg-gray-100 text-gray-800' };
    
    try {
      let end;
      
      // Handle string dates in our custom format
      if (typeof endDate === 'string') {
        end = parseCustomDateFormat(endDate);
      } else {
        end = new Date(endDate);
      }
      
      if (!end || isNaN(end.getTime())) {
        return { text: 'Invalid Date', className: 'bg-gray-100 text-gray-800' };
      }
      
      const now = new Date();
      
      if (end < now) {
        return { text: 'Expired', className: 'bg-red-100 text-red-800' };
      }
      
      const daysLeft = Math.floor((end - now) / (1000 * 60 * 60 * 24));
      
      if (daysLeft <= 7) {
        return { text: `${daysLeft} days left`, className: 'bg-yellow-100 text-yellow-800' };
      }
      
      if (daysLeft <= 30) {
        return { text: `${daysLeft} days left`, className: 'bg-green-100 text-green-800' };
      }
      
      return { text: 'Active', className: 'bg-green-100 text-green-800' };
    } catch (error) {
      console.error("Error calculating status:", error);
      return { text: 'Error', className: 'bg-gray-100 text-gray-800' };
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Membership Management</h1>
          <button
            onClick={() => setCurrentPage('UserManagement')}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add User
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 col-span-1">
            <h3 className="text-gray-500 text-sm">Active Memberships</h3>
            <p className="text-3xl font-semibold text-gray-800">{stats.totalActive}</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 col-span-1">
            <h3 className="text-gray-500 text-sm">Monthly Plans</h3>
            <p className="text-3xl font-semibold text-blue-600">{stats.monthly}</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 col-span-1">
            <h3 className="text-gray-500 text-sm">Six Month Plans</h3>
            <p className="text-3xl font-semibold text-purple-600">{stats.sixMonth}</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 col-span-1">
            <h3 className="text-gray-500 text-sm">Yearly Plans</h3>
            <p className="text-3xl font-semibold text-green-600">{stats.yearly}</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 col-span-1">
            <h3 className="text-gray-500 text-sm">Total Revenue</h3>
            <p className="text-3xl font-semibold text-indigo-600">{formatCurrency(stats.revenue)}</p>
          </div>
        </div>
        
        {/* Revenue Chart */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-gray-700 text-lg font-semibold mb-4">Revenue Trend (This Year)</h3>
          <div className="h-72">
            <Line 
              data={revenueData} 
              options={{ 
                maintainAspectRatio: false,
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: { color: 'rgba(0, 0, 0, 0.05)' },
                    ticks: {
                      callback: (value) => `$${value}`
                    }
                  },
                  x: {
                    grid: { color: 'rgba(0, 0, 0, 0.05)' }
                  }
                },
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { padding: 20, boxWidth: 15 }
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => `Revenue: $${context.raw}`
                    }
                  }
                }
              }} 
            />
          </div>
        </div>
        
        {/* Search and Filter */}
        <div className="mb-6 flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[300px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search users or institutions..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="relative flex items-center">
            <select
              className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value)}
            >
              <option value="all">All Plans</option>
              <option value="monthly">Monthly</option>
              <option value="sixMonth">Six Month</option>
              <option value="yearly">Yearly</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          
          <div className="relative flex items-center">
            <select
              className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
        
        {/* Memberships Table */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredMemberships.length > 0 ? (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Institution</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMemberships.map((membership) => {
                  const statusInfo = calculateStatus(membership.endDate);
                  
                  return (
                    <tr key={membership.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                            <span className="text-indigo-700 font-medium">
                              {membership.displayName?.charAt(0) || membership.email?.charAt(0) || '?'}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{membership.displayName || 'Unnamed User'}</div>
                            <div className="text-sm text-gray-500">{membership.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{membership.instituteName || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSubscriptionBadgeClass(membership.planType, membership.status)}`}>
                          {formatPlanName(membership.planType)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatCurrency(membership.amount)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(membership.startDate)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(membership.endDate)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusInfo.className}`}>
                          {statusInfo.text}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button 
                          onClick={() => openEditModal(membership)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No memberships found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No membership data matching your criteria.
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setFilterPlan('all');
                  setFilterStatus('all');
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Edit Membership Modal */}
      {showEditModal && selectedMembership && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Edit Membership</h2>
              
              <form onSubmit={handleUpdateMembership} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    User
                  </label>
                  <div className="flex items-center p-2 border border-gray-200 rounded-lg bg-gray-50">
                    <div className="h-8 w-8 bg-indigo-100 rounded-full flex items-center justify-center">
                      <span className="text-indigo-700 font-medium">
                        {selectedMembership.displayName?.charAt(0) || selectedMembership.email?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium">{selectedMembership.displayName}</div>
                      <div className="text-xs text-gray-500">{selectedMembership.email}</div>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subscription Plan
                    </label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-lg"
                      value={selectedMembership.planType}
                      onChange={(e) => setSelectedMembership({...selectedMembership, planType: e.target.value})}
                    >
                      {subscriptionPlans.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} (${plan.price})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-lg"
                      value={selectedMembership.status}
                      onChange={(e) => setSelectedMembership({...selectedMembership, status: e.target.value})}
                    >
                      {subscriptionStatuses.map(status => (
                        <option key={status.id} value={status.id}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <div className="flex">
                    <input
                      type="text"
                      className="w-full p-2 border border-gray-300 rounded-lg"
                      value={selectedMembership.startDate ? formatDate(selectedMembership.startDate) : ''}
                      disabled
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Current format: {formatDate(new Date())}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order ID (read only)
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-lg bg-gray-50"
                    value={selectedMembership.orderId || 'N/A'}
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">A new order ID will be generated if the plan type changes</p>
                </div>
                
                <div className="mt-6 flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipManagement;